<template>
  <div class="admin-login-container">
    <div class="admin-login-card">
      <div class="admin-header">
        <div class="admin-logo">
          <img src="/images/aslan-energy-logo-hq.jpg" alt="Aslan Energy" class="admin-logo-img" />
          <h1>Admin Panel</h1>
        </div>
        <p class="admin-subtitle">Aslan Energy Yönetim Paneli</p>
      </div>

      <form @submit.prevent="handleLogin" class="admin-form">
        <div class="form-group">
          <label for="username">Kullanıcı Adı</label>
          <input 
            type="text" 
            id="username"
            v-model="credentials.username"
            required 
            :disabled="isLoading"
            class="admin-input"
            placeholder="Admin kullanıcı adını girin"
          />
        </div>

        <div class="form-group">
          <label for="password">Şifre</label>
          <input 
            type="password" 
            id="password"
            v-model="credentials.password"
            required 
            :disabled="isLoading"
            class="admin-input"
            placeholder="Admin şifresini girin"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button 
          type="submit" 
          :disabled="isLoading"
          class="admin-login-btn"
        >
          <span v-if="isLoading">Giriş Yapılıyor...</span>
          <span v-else>Giriş Yap</span>
        </button>
      </form>

      <div class="admin-footer">
        <a href="#" @click="goToHome" class="back-to-site">
          ← Ana Siteye Dön
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { adminApi } from '@/services/adminApi'

const emit = defineEmits(['navigate'])

const credentials = reactive({
  username: '',
  password: ''
})

const isLoading = ref(false)
const error = ref('')

const handleLogin = async () => {
  isLoading.value = true
  error.value = ''

  try {
    const response = await adminApi.login(credentials.username, credentials.password)
    
    if (response.success) {
      localStorage.setItem('admin_token', response.token)
      localStorage.setItem('admin_user', JSON.stringify(response.user))
      
      // Admin dashboard'a yönlendir
      emit('navigate', 'admin-dashboard')
    } else {
      error.value = response.message || 'Giriş başarısız'
    }
  } catch (err) {
    error.value = 'Sunucu hatası: ' + (err.message || 'Bilinmeyen hata')
  } finally {
    isLoading.value = false
  }
}

const goToHome = () => {
  emit('navigate', 'home')
}

onMounted(() => {
  // Hide main header for admin pages
  document.body.classList.add('admin-page')
})

onUnmounted(() => {
  // Remove admin class when leaving admin page
  document.body.classList.remove('admin-page')
})
</script>

<style scoped>
.admin-login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  margin: 0;
  position: relative;
  top: 0;
}

.admin-login-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
}

.admin-login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #F7C52D, #E6B800, #CC9900);
}

.admin-header {
  text-align: center;
  margin-bottom: 32px;
}

.admin-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.admin-logo-img {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.admin-logo h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
}

.admin-subtitle {
  margin: 8px 0 0 0;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.admin-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.admin-input {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  background: #f9fafb;
}

.admin-input:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.admin-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
}

.admin-login-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.admin-login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.admin-login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.admin-footer {
  margin-top: 24px;
  text-align: center;
}

.back-to-site {
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s ease;
}

.back-to-site:hover {
  color: #3b82f6;
}

@media (max-width: 480px) {
  .admin-login-card {
    padding: 24px;
    margin: 10px;
  }
  
  .admin-logo h1 {
    font-size: 20px;
  }
}
</style>